
import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import config from "../config";
import AvailabilityPicker from "../components/AvailabilityPicker";
import CrisisNotice from "../components/CrisisNotice";
import LoadingSpinner from "../components/LoadingSpinner";
import useBooking from "../hooks/useBooking";
import useServices from "../hooks/useServices";
import { redirectToPayment } from "../services/api/paymentService";
import { getErrorMessage, isErrorCode, isSlotUnavailableError } from "../services/errors/errorHandler";
import { ERROR_CODES } from "../services/errors/ApiError";
import { formatBusinessDateTime } from "../services/utils/dateUtils";
import { checkoutSchema } from "../utils/validationSchemas";
import { registerByEmailHref } from "../data/practice";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// "$150", "$150.50", "Free"
function formatFee(amount) {
  const n = Number(amount) || 0;
  if (n <= 0) return "Free";
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

const DEFAULT_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  notes: "",
  policyAccepted: false,
  website: "",
};

// Error text is linked to its field for screen readers.
function FieldError({ id, error }) {
  if (!error) return null;
  return (
    <p id={id} role="alert" style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '0.25rem' }}>
      {error.message}
    </p>
  );
}

const fieldA11y = (name, errors, hintId) => {
  const describedBy = [errors[name] ? `${name}-error` : null, hintId].filter(Boolean).join(" ");
  return {
    id: `checkout-${name}`,
    "aria-invalid": errors[name] ? "true" : "false",
    "aria-describedby": describedBy || undefined,
  };
};

const errorBorder = (error) => ({ borderColor: error ? '#d32f2f' : '' });

export default function Checkout() {
  const q = useQuery();
  const navigate = useNavigate();
  const { bookableServices, getService } = useServices();
  const paymentsEnabled = config.features.paymentsEnabled;

  // ?service= preselects; an unknown slug falls back to the first bookable
  // service so the <select> and the booking always agree. A known service
  // that is not booked online (bookable_online false from the API) is never
  // swapped silently: the page says so and offers email registration until
  // the visitor chooses to book something else.
  const [serviceSlug, setServiceSlug] = useState(() => q.get("service") || "");
  const requested = getService(serviceSlug);
  const notBookable = requested && requested.bookableOnline === false ? requested : null;
  const service = bookableServices.find((s) => s.slug === serviceSlug) || bookableServices[0] || null;
  const activeSlug = service?.slug || "";

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    resolver: yupResolver(checkoutSchema),
    mode: 'onChange', // Validate on change for better UX
    defaultValues: DEFAULT_VALUES,
  });

  const [selectedSlot, setSelectedSlot] = useState(null); // {startISO,endISO}
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  const inFlight = useRef(false);

  const { createBooking, loading: bookingLoading } = useBooking();
  const busy = bookingLoading || isSubmitting;

  const fee = Number(service?.price) || 0;
  const takesOnlinePayment = paymentsEnabled && fee > 0;
  // Contract v1.3: an online booking is a request the owner confirms.
  const submitLabel = takesOnlinePayment ? "Proceed to Payment" : "Request this time";

  const onChangeService = (e) => {
    setServiceSlug(e.target.value);
    // clear any prev selection if service changes
    setSelectedSlot(null);
  };

  const submit = async (formData) => {
    // One booking request at a time, whatever triggered the submit.
    if (inFlight.current) return;

    if (!selectedSlot) {
      toast.error("Please select an available time before proceeding.");
      return;
    }

    inFlight.current = true;
    let loadingToast;
    try {
      loadingToast = toast.loading("Sending your request...");

      // Contract v1: the slot's startISO is sent unchanged as start_time.
      const result = await createBooking(formData, selectedSlot, activeSlug);

      // useBooking already stored the PII-free summary for the confirmation page.
      if (result.payment) {
        toast.success("Redirecting to payment...");
        redirectToPayment(result.payment);
        return;
      }
      if (result.paymentError) {
        toast.error(result.paymentError, { duration: 8000 });
      } else {
        // An older backend still confirms at once; v1.3 answers 'scheduled'.
        toast.success(result.status === "confirmed" ? "Booking confirmed!" : "Request sent!");
      }
      navigate('/booking-confirmation');
    } catch (error) {
      if (isSlotUnavailableError(error) || isErrorCode(error, ERROR_CODES.NOTICE_PERIOD)) {
        // The chosen time is gone: drop it and reload the day's slots.
        setSelectedSlot(null);
        setAvailabilityRefreshKey((n) => n + 1);
      }
      toast.error(getErrorMessage(error) || "Failed to create booking. Please try again.");
    } finally {
      if (loadingToast !== undefined) toast.dismiss(loadingToast);
      inFlight.current = false;
    }
  };

  const selectedWhen = selectedSlot ? formatBusinessDateTime(selectedSlot.startISO) : "";

  return (
    <section className="section white">
      <div className="container">
        <h1>Book a Session</h1>
        <p className="muted">Select your service, pick a time, and enter your details.</p>

        {notBookable ? (
          <div className="card checkout-not-bookable" role="status" style={{marginTop:"1rem"}}>
            <h2 className="checkout-step">{notBookable.title} is not booked online</h2>
            <p style={{marginTop:".5rem"}}>
              Registration for {notBookable.title} is by email, not the online calendar.
              Email me and I'll send you the next dates and how to join.
            </p>
            <div style={{display:"flex", gap:".5rem", marginTop:"1rem", flexWrap:"wrap"}}>
              <a className="btn-cta" href={registerByEmailHref(notBookable.title)}>Email to Register</a>
              <button type="button" className="btn-cta" onClick={() => setServiceSlug("")}>
                Book a different session
              </button>
            </div>
          </div>
        ) : (
        <div className="checkout-grid" style={{marginTop:"1rem"}}>
          {/* form + availability */}
          <form className="card checkout-form" onSubmit={handleSubmit(submit)} noValidate>
            <h2 className="checkout-step"><label htmlFor="checkout-service">1) Service</label></h2>
            <select
              id="checkout-service"
              value={activeSlug}
              onChange={onChangeService}
              disabled={busy}
              style={{marginTop:".5rem"}}
            >
              {bookableServices.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.title} — {formatFee(s.price)} • {s.duration}
                </option>
              ))}
            </select>

            <h2 className="checkout-step" style={{marginTop:"1rem"}}>2) Pick an Available Time</h2>
            <AvailabilityPicker
              serviceSlug={activeSlug}
              onSelect={setSelectedSlot}
              selectedStartISO={selectedSlot?.startISO || null}
              onDayChange={() => setSelectedSlot(null)}
              disabled={busy}
              refreshKey={availabilityRefreshKey}
            />

            <h2 className="checkout-step" style={{marginTop:"1rem"}}>3) Your Selected Time</h2>
            <p aria-live="polite" style={{marginTop:".25rem"}}>
              {selectedWhen || <span className="muted">No time selected yet. Pick a day and a time above.</span>}
            </p>

            <h2 className="checkout-step" style={{marginTop:"1rem"}}>4) Your Details</h2>
            <div className="name-grid">
              <div>
                <label htmlFor="checkout-firstName">First name</label>
                <input
                  type="text"
                  autoComplete="given-name"
                  {...register("firstName")}
                  {...fieldA11y("firstName", errors)}
                  aria-required="true"
                  style={errorBorder(errors.firstName)}
                />
                <FieldError id="firstName-error" error={errors.firstName} />
              </div>
              <div>
                <label htmlFor="checkout-lastName">Last name</label>
                <input
                  type="text"
                  autoComplete="family-name"
                  {...register("lastName")}
                  {...fieldA11y("lastName", errors)}
                  aria-required="true"
                  style={errorBorder(errors.lastName)}
                />
                <FieldError id="lastName-error" error={errors.lastName} />
              </div>
            </div>
            <div style={{marginTop:".75rem"}}>
              <label htmlFor="checkout-email">Email</label>
              <input
                type="email"
                autoComplete="email"
                {...register("email")}
                {...fieldA11y("email", errors, "email-hint")}
                aria-required="true"
                style={errorBorder(errors.email)}
              />
              <p id="email-hint" className="muted" style={{fontSize:".875rem", marginTop:".25rem"}}>
                Your confirmation and calendar invite are sent here.
              </p>
              <FieldError id="email-error" error={errors.email} />
            </div>
            <div style={{marginTop:".75rem"}}>
              <label htmlFor="checkout-phone">Phone <span className="muted">(optional)</span></label>
              <input
                type="tel"
                autoComplete="tel"
                {...register("phone")}
                {...fieldA11y("phone", errors)}
                style={errorBorder(errors.phone)}
              />
              <FieldError id="phone-error" error={errors.phone} />
            </div>
            <div style={{marginTop:".75rem"}}>
              <label htmlFor="checkout-notes">Anything you'd like me to know? <span className="muted">(optional)</span></label>
              <textarea
                rows={4}
                {...register("notes")}
                {...fieldA11y("notes", errors)}
                style={errorBorder(errors.notes)}
              />
              <FieldError id="notes-error" error={errors.notes} />
            </div>

            {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="checkout-website">Website</label>
              <input id="checkout-website" type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
            </div>

            <div className="checkout-consent" style={{marginTop:"1rem"}}>
              <input
                type="checkbox"
                {...register("policyAccepted")}
                {...fieldA11y("policyAccepted", errors)}
                aria-required="true"
              />
              <label htmlFor="checkout-policyAccepted">
                I agree to the{" "}
                <a href="/policies#terms" target="_blank" rel="noopener noreferrer" className="text-link">Terms</a>,{" "}
                <a href="/policies#privacy" target="_blank" rel="noopener noreferrer" className="text-link">Privacy Policy</a>{" "}
                and{" "}
                <a href="/policies#refund" target="_blank" rel="noopener noreferrer" className="text-link">Cancellation Policy</a>
                <span className="sr-only"> (links open in a new tab)</span>
              </label>
            </div>
            <FieldError id="policyAccepted-error" error={errors.policyAccepted} />

            <button
              className="btn-cta"
              style={{marginTop:"1rem"}}
              type="submit"
              disabled={busy || !isValid || !selectedSlot || !service}
            >
              {busy ? "Processing..." : submitLabel}
            </button>
            {!selectedSlot && !busy && (
              <p className="muted" style={{fontSize:".875rem", marginTop:".25rem"}}>
                Pick a time above to continue.
              </p>
            )}
            {!takesOnlinePayment && (
              <p className="muted" style={{fontSize:".875rem", marginTop:".25rem"}}>
                Jasmine confirms each booking by email. The time is held for you while she reviews your request.
              </p>
            )}
            {busy && (
              <div style={{ marginTop: "1rem" }}>
                <LoadingSpinner size="small" text="Sending your request..." />
              </div>
            )}

            <CrisisNotice variant="form" />
          </form>

          {/* summary */}
          <div className="card checkout-summary">
            <h2 className="checkout-step">Summary</h2>
            {service && (
              <>
                <p><strong>{service.title}</strong></p>
                <p>{service.duration}</p>
                <p style={{marginTop:".5rem"}}><strong>Fee: {formatFee(fee)}</strong></p>
              </>
            )}
            {selectedWhen && (
              <p className="muted" style={{marginTop:".5rem"}}>
                Selected time: {selectedWhen}
              </p>
            )}
            <p className="muted" style={{marginTop:".75rem"}}>
              {takesOnlinePayment
                ? "After booking you'll be taken to the secure payment page to pay online."
                : fee > 0
                  ? "No payment is taken now. Pay by bank transfer or cheque before or at your session."
                  : "No payment is needed for this session."}
            </p>
          </div>
        </div>
        )}
      </div>
    </section>
  );
}
